/**
 * Customer contacts list + slide-out edit panel (18.4).
 */

import { useState } from 'react';
import useEntityList from '../../hooks/useEntityList';
import { fetchContacts, createContact, updateContact, deleteContact } from '../../api/customers';
import { fetchCustomers } from '../../api/customers';
import { SlideOutPanel, Button, LoadingSpinner, EmptyState, Pagination } from '../common';

export default function ContactList({ search, showDeleted }) {
  const [customerFilter, setCustomerFilter] = useState('');
  const { items, loading, totalCount, page, setPage, refresh } = useEntityList(
    fetchContacts,
    { search, showDeleted, extraParams: customerFilter ? { customer: customerFilter } : {} }
  );
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customersLoaded, setCustomersLoaded] = useState(false);

  async function loadCustomers() {
    if (customersLoaded) return;
    try {
      const resp = await fetchCustomers({ page_size: 200 });
      setCustomers(resp.data || resp.results || resp || []);
      setCustomersLoaded(true);
    } catch {
      // ignore
    }
  }

  function openCreate() {
    loadCustomers();
    setForm({
      customer: customerFilter || '',
      first_name: '', last_name: '',
      email: '', phone: '', role_title: '',
      is_primary: false, notes: '',
    });
    setCreating(true);
    setSelected(null);
  }

  function openEdit(item) {
    loadCustomers();
    setForm({
      customer: item.customer || '',
      first_name: item.first_name || '',
      last_name: item.last_name || '',
      email: item.email || '',
      phone: item.phone || '',
      role_title: item.role_title || '',
      is_primary: !!item.is_primary,
      notes: item.notes || '',
    });
    setSelected(item);
    setCreating(false);
  }

  function closePanel() { setSelected(null); setCreating(false); }

  async function handleSave() {
    setSaving(true);
    try {
      if (creating) {
        await createContact(form);
      } else {
        await updateContact(selected.id, form);
      }
      closePanel();
      refresh();
    } catch (err) {
      alert(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm('Delete this contact?')) return;
    try {
      await deleteContact(selected.id);
      closePanel();
      refresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed');
    }
  }

  if (loading) return <div className="flex justify-center py-10"><LoadingSpinner /></div>;

  return (
    <div>
      <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-3">
        <Button size="sm" onClick={openCreate}>+ New Contact</Button>
        <select
          className="border border-gray-300 rounded-md px-2 py-1 text-sm"
          value={customerFilter}
          onChange={(e) => setCustomerFilter(e.target.value)}
        >
          <option value="">All customers</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        {!customersLoaded && (
          <button onClick={loadCustomers} className="text-xs text-blue-600">Load filter</button>
        )}
      </div>

      {items.length === 0 ? (
        <EmptyState title="No contacts" description="Add contacts for your customers." />
      ) : (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Phone</th>
                <th className="px-4 py-2">Role / Title</th>
                <th className="px-4 py-2 text-center">Primary</th>
                <th className="px-4 py-2">Customer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => openEdit(item)}
                  className={`hover:bg-blue-50 cursor-pointer ${item.deleted_at ? 'opacity-50 line-through' : ''}`}
                >
                  <td className="px-4 py-2 font-medium text-foreground">
                    {item.display_name || `${item.first_name || ''} ${item.last_name || ''}`.trim() || '(unnamed)'}
                  </td>
                  <td className="px-4 py-2 text-gray-500">{item.email || '—'}</td>
                  <td className="px-4 py-2 text-gray-500">{item.phone || '—'}</td>
                  <td className="px-4 py-2 text-gray-500">{item.role_title || '—'}</td>
                  <td className="px-4 py-2 text-center">
                    {item.is_primary && <span className="text-green-600 text-xs font-medium">Yes</span>}
                  </td>
                  <td className="px-4 py-2 text-gray-400 text-xs">{item.customer_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2">
            <Pagination current={page} total={Math.ceil(totalCount / 200)} onChange={setPage} />
          </div>
        </>
      )}

      <SlideOutPanel
        open={!!selected || creating}
        onClose={closePanel}
        title={creating ? 'New Contact' : `Edit: ${selected?.display_name || selected?.first_name || ''}`}
        width="max-w-md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={form.customer || ''}
              onChange={(e) => setForm({ ...form, customer: e.target.value })}
            >
              <option value="">Select...</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextInput label="First Name" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} />
            <TextInput label="Last Name" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} />
          </div>
          <TextInput label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" />
          <TextInput label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
          <TextInput label="Role / Title" value={form.role_title} onChange={(v) => setForm({ ...form, role_title: v })} />
          <TextInput label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} multiline />
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_primary || false}
              onChange={(e) => setForm({ ...form, is_primary: e.target.checked })}
              className="rounded border-gray-300 text-blue-600"
            />
            <label className="text-sm text-gray-700">Primary contact</label>
          </div>

          <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
            <Button loading={saving} onClick={handleSave}>Save</Button>
            <Button variant="ghost" onClick={closePanel}>Cancel</Button>
            {selected && (
              <Button variant="danger" size="sm" className="ml-auto" onClick={handleDelete}>Delete</Button>
            )}
          </div>
        </div>
      </SlideOutPanel>
    </div>
  );
}

function TextInput({ label, value, onChange, type = 'text', multiline = false }) {
  const cls = "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500";
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {multiline ? (
        <textarea className={cls} rows={2} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} className={cls} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}
